"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import type { Bet, Transfer } from "@/lib/data";
import { PlatformError } from "@/lib/api/client";
import { useRewardTransactions, useTransactions } from "@/lib/api/hooks";
import { messageFor } from "@/lib/api/messages";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { SectionEmpty, SectionError } from "@/components/ui/states";
import { cn } from "@/lib/cn";
import { EASE, spring } from "@/lib/motion";
import { Icon } from "@/components/ui/Icon";
import { Panel, PanelHeader } from "@/components/ui/primitives";
import { explorerTxUrl } from "@/lib/explorers";
import { ProfileCard, type ProfileCardSide } from "./ProfileCard";
import { VerifyOnChainModal, type VerifySubject } from "./VerifyOnChainModal";
import { ProfileAvatar } from "@/components/profile/ProfileAvatar";

const TABS = ["Live Bets", "Live Transactions", "Live Rewards"] as const;

const NEAR_BOTTOM_ROWS = 4;


export function LiveStats({ bets }: { bets: Bet[] }) {
  const [tab, setTab] = useState<(typeof TABS)[number]>("Live Bets");

  return (
    <Panel className="overflow-hidden">
      <PanelHeader icon="chart" title="Live Stats" />

      <div className="flex flex-col items-start gap-[16px] p-[16px]">
        <div className="flex w-full items-start gap-[8px]">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                "relative flex h-[40px] items-center rounded-rounded px-[20px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
                tab === t ? "text-fg-on-primary" : "bg-elevation-2 text-fg-subtle hover:text-fg",
              )}
            >
              {tab === t && (
                <motion.span layoutId="stats-tab" transition={spring} className="absolute inset-0 rounded-rounded bg-brand" />
              )}
              <span className="relative">{t}</span>
            </button>
          ))}
        </div>

        {tab === "Live Bets" && <BetRows bets={bets} />}
        {tab === "Live Transactions" && <TransactionRows />}
        {tab === "Live Rewards" && <RewardRows />}
      </div>
    </Panel>
  );
}

/* The game a bet was placed on. A game from the API has a slug, and the
   whole cell — art and title — is a link to it, since that is what a name
   in a feed is for. The stand-in games the mock rows use have none, and
   stay text. */
function GameCell({ game }: { game: Bet["game"] }) {
  const inner = (
    <>
      <span
        className="relative h-[36px] w-[36px] shrink-0 overflow-hidden rounded-sm"
        style={{ background: `linear-gradient(155deg, ${game.from}, ${game.to})` }}
      >
        {game.thumbnailUrl && <RemoteImage src={game.thumbnailUrl} width={36} height={36} />}
      </span>
      <span className="max-w-[190px] truncate font-bold text-fg transition-colors">{game.title}</span>
    </>
  );

  if (!game.slug) {
    return <span className="flex items-center gap-[8px]">{inner}</span>;
  }

  return (
    <Link
      href={`/game/${game.slug}`}
      title={`Open ${game.title}`}
      className="flex cursor-pointer items-center gap-[8px] hover:[&>span:last-child]:text-brand-fg-accent"
    >
      {inner}
    </Link>
  );
}

export function BetRows({ bets }: { bets: Bet[] }) {
  const [verify, setVerify] = useState<VerifySubject | null>(null);

  return (
    <>
    <div className="w-full overflow-hidden rounded-xl border border-border">
      {/* The kit's compact table: below xl the middle columns fold away, leaving
          Game / Payout / Verify, so a narrow rail reads without a sideways
          scroll. The min width only applies once every column is shown. */}
      <div className="scrollbar-none overflow-x-auto">
        <table className="w-full border-collapse xl:min-w-[1000px]">
          <thead>
            <tr className="h-[42px] border-b border-border bg-elevation-2 text-[12px] font-bold leading-[1.6] text-fg-subtle">
              <th className="pl-[14px] text-left">GAME</th>
              <th className="hidden w-[160px] text-left xl:table-cell">USER</th>
              <th className="hidden w-[90px] text-left xl:table-cell">TIME</th>
              <th className="hidden w-[120px] text-left xl:table-cell">BET</th>
              <th className="hidden w-[100px] text-right xl:table-cell">MULTIPLIER</th>
              <th className="w-[120px] text-right">PAYOUT</th>
              <th className="w-[160px] pr-[14px] text-right">VERIFY</th>
            </tr>
          </thead>
          <tbody>
            {bets.map((b, i) => (
              <Row key={i} index={i}>
                <td className="pl-[14px]">
                  <GameCell game={b.game} />
                </td>
                <td className="hidden w-[160px] xl:table-cell">
                  <PlayerCell
                    name={b.user}
                    accountId={b.accountId}
                    side={i >= bets.length - NEAR_BOTTOM_ROWS ? "top" : "bottom"}
                  />
                </td>
                <td className="hidden w-[90px] whitespace-nowrap font-medium text-fg-subtle xl:table-cell">{b.time}</td>
                <td className="hidden w-[120px] whitespace-nowrap font-bold text-fg xl:table-cell">{b.bet}</td>
                {/* Both figures colour by value: the kit's highlight when the
                    round returned something, muted when it did not. The payout
                    used to be highlighted unconditionally, so a settled loss
                    showed a glowing 0.00 — the one number that must never read
                    as a win. A multiplier at or below 1x returned no more than
                    the stake, so it is muted on the same reasoning. */}
                <td
                  className={cn(
                    "hidden w-[100px] whitespace-nowrap text-right font-medium xl:table-cell",
                    b.ratio > 1 ? "text-glow-highlight" : "text-fg-subtle",
                  )}
                >
                  {b.multiplier}
                </td>
                <td
                  className={cn(
                    "w-[120px] whitespace-nowrap text-right font-bold",
                    b.credited ? "text-glow-highlight" : "text-fg-subtle",
                  )}
                >
                  {b.payout}
                </td>
                <td className="w-[160px] pr-[14px]">
                  <VerifyCell
                    shortHash={b.hash}
                    onInspect={
                      b.hashes && b.hashes.length > 0
                        ? () => setVerify({ hashes: b.hashes as string[], chainId: b.chainId })
                        : undefined
                    }
                  />
                </td>
              </Row>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    <VerifyOnChainModal subject={verify} onClose={() => setVerify(null)} />
    </>
  );
}

function TransactionRows() {
  const feed = useTransactions();

  return (
    <TransferRows
      kindLabel="TYPE"
      feed={feed}
      emptyTitle="No transactions yet"
      emptyBody="Deposits and withdrawals appear here as they settle."
      errorTitle="The transaction feed is unavailable"
      highlightIncoming={false}
    />
  );
}

function RewardRows() {
  const feed = useRewardTransactions();

  return (
    <TransferRows
      kindLabel="REWARD"
      feed={feed}
      emptyTitle="No rewards paid out yet"
      emptyBody="Rakeback, cashback and bonus payouts appear here as they are claimed."
      errorTitle="The reward feed is unavailable"
    />
  );
}

export function TransferRows({
  kindLabel,
  feed,
  emptyTitle,
  emptyBody,
  errorTitle,
  highlightIncoming = true,
}: {
  kindLabel: string;
  feed: { transfers: Transfer[]; isPending: boolean; error: unknown };
  emptyTitle: string;
  emptyBody: string;
  errorTitle: string;
  /* Whether an incoming amount is highlighted. A reward payout is money won
     and reads as such; a deposit or withdrawal is money moved, where the
     direction is already stated by the row and the colour only adds noise. */
  highlightIncoming?: boolean;
}) {
  if (feed.isPending) {
    return <div className="h-[180px] w-full animate-pulse rounded-xl bg-elevation-2" />;
  }

  if (feed.error) {
    return (
      <SectionError
        title={errorTitle}
        body={messageFor(
          feed.error instanceof PlatformError ? feed.error : new PlatformError(0, "network", ""),
          "bets",
        )}
      />
    );
  }

  if (feed.transfers.length === 0) {
    return <SectionEmpty icon="chart" title={emptyTitle} body={emptyBody} />;
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border">
      <div className="scrollbar-none overflow-x-auto">
        <table className="w-full min-w-[1000px] border-collapse">
          <thead>
            <tr className="h-[42px] border-b border-border bg-elevation-2 text-[12px] font-bold leading-[1.6] text-fg-subtle">
              <th className="w-[220px] pl-[14px] text-left">{kindLabel}</th>
              <th className="w-[180px] text-left">USER</th>
              <th className="w-[140px] text-left">TIME</th>
              <th className="text-left">STATUS</th>
              <th className="w-[245px] text-right">AMOUNT</th>
              <th className="w-[200px] pr-[14px] text-right">VERIFY</th>
            </tr>
          </thead>
          <tbody>
            {feed.transfers.map((transfer, index) => (
              <Row key={transfer.id} index={index}>
                <td className="w-[220px] pl-[14px] font-bold capitalize text-fg">{transfer.kind}</td>
                <td className="w-[180px]">
                  <PlayerCell
                    name={transfer.user}
                    accountId={transfer.accountId}
                    side={index >= feed.transfers.length - NEAR_BOTTOM_ROWS ? "top" : "bottom"}
                  />
                </td>
                <td className="w-[140px] font-medium text-fg-subtle">{transfer.time}</td>
                <td className="font-medium capitalize text-fg-subtle">{transfer.status}</td>
                <td
                  className={cn(
                    "w-[245px] text-right font-bold",
                    highlightIncoming && transfer.incoming ? "text-glow-highlight" : "text-fg",
                  )}
                >
                  {transfer.amount}
                </td>
                <td className="w-[200px] pr-[14px]">
                  <span className="flex items-center justify-end gap-[4px]">
                    <VerifyCell
                      shortHash={transfer.hash}
                      href={transfer.fullHash ? explorerTxUrl(transfer.chainId, transfer.fullHash) : null}
                    />
                  </span>
                </td>
              </Row>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Row({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  return (
    <motion.tr
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.35, ease: EASE, delay: Math.min(index * 0.03, 0.3) }}
      className="h-[51px] border-b border-border bg-elevation-2 text-[14px] leading-[1.6] transition-colors last:border-0 hover:bg-elevation-3"
    >
      {children}
    </motion.tr>
  );
}

export function PlayerCell({
  name,
  accountId,
  side = "bottom",
}: {
  name: string;
  accountId?: string;
  side?: ProfileCardSide;
}) {
  const anonymous = name === "Hidden";
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLSpanElement | null>(null);

  return (
    <span
      ref={setAnchor}
      className="relative inline-flex items-center font-bold"
    >
      {anonymous ? (
        <span className="inline-flex items-center gap-[4px]">
          <Icon name="incognito" size={26} className="text-fg-subtle" />
          <span className="text-fg-on-disabled">{name}</span>
        </span>
      ) : (
        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="inline-flex items-center gap-[4px]"
        >
          <ProfileAvatar accountId={accountId} name={name} size={26} />
          <span className="text-fg">{name}</span>
        </button>
      )}
      <AnimatePresence>
        {!anonymous && open && (
          <ProfileCard
            name={name}
            accountId={accountId}
            onClose={() => setOpen(false)}
            side={side}
            anchor={anchor}
          />
        )}
      </AnimatePresence>
    </span>
  );
}


/* The kit's VERIFY cell. On a bet — the Live Bets frame — the hash opens the
   in-app Verify On-Chain modal behind an eye, because a bet can record several
   hashes. On a transfer or reward — the Live Transactions and Rewards frames —
   a single hash links straight out to the block explorer behind an open-in-new
   icon. Either way the hash sits in the brand accent so it reads as a link, and
   a row with no hash shows a dash. */
export function VerifyCell({
  shortHash,
  onInspect,
  href,
}: {
  shortHash: string;
  onInspect?: () => void;
  href?: string | null;
}) {
  if (shortHash === "—" || (!onInspect && !href)) {
    return <span className="flex items-center justify-end font-medium text-fg-subtle">&mdash;</span>;
  }

  const inner = (
    <>
      <span className="w-[106px] truncate text-right font-bold text-brand-fg-accent">{shortHash}</span>
      <Icon name={onInspect ? "eye" : "external"} size={16} className="shrink-0 text-fg-subtle transition-colors group-hover:text-fg" />
    </>
  );

  const className =
    "group flex w-full items-center justify-end gap-[6px] transition-colors [&:hover_span]:text-fg";

  if (onInspect) {
    return (
      <button type="button" onClick={onInspect} aria-haspopup="dialog" aria-label="Verify on-chain" className={className}>
        {inner}
      </button>
    );
  }

  return (
    <a href={href ?? undefined} target="_blank" rel="noreferrer noopener" aria-label="Open in block explorer" className={className}>
      {inner}
    </a>
  );
}
